import Button from '@app-studio-web/ui/components/Button'
import Layout from '@app-studio-web/ui/components/Layout'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, useSubmit } from 'react-router-dom'

export default function QuestionStressLevelPage() {
  const { t } = useTranslation()
  const submit = useSubmit()
  const [stressLevel, setStressLevel] = useState<number>(5)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'hidden'
    hiddenInput.name = 'answer'
    hiddenInput.value = stressLevel.toString()
    event.currentTarget.appendChild(hiddenInput)

    try {
      sessionStorage.setItem('onboarding_stress', stressLevel.toString())
    } catch (error) {
      Sentry.captureException(error)
    }

    submit(event.currentTarget)
    event.currentTarget.removeChild(hiddenInput)
  }

  const getStressLabel = () => {
    if (stressLevel <= 3) return t('pages.onboarding.stressLevel.labelLow')
    if (stressLevel <= 6) return t('pages.onboarding.stressLevel.labelModerate')
    if (stressLevel <= 8) return t('pages.onboarding.stressLevel.labelHigh')
    return t('pages.onboarding.stressLevel.labelVeryHigh')
  }

  const getStressColor = () => {
    if (stressLevel <= 3) return 'from-green-400 to-green-500'
    if (stressLevel <= 6) return 'from-yellow-400 to-orange-400'
    if (stressLevel <= 8) return 'from-orange-400 to-red-400'
    return 'from-red-500 to-red-600'
  }

  return (
    <Layout>
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-[#FFFFFF] to-[#EFFBFF]" />
      <div className="flex h-full flex-col">
        <div className="mt-11 shrink-0 text-center">
          <h1 className="custom-heading mb-4" data-testid="label-header">
            {t('pages.onboarding.stressLevel.heading')}
          </h1>
          <p className="text-gray-600">
            {t('pages.onboarding.stressLevel.subheading')}
          </p>
        </div>

        <div className="flex grow flex-col items-center justify-center pb-10">
          <div
            className={`mb-8 flex h-32 w-32 items-center justify-center rounded-full bg-gradient-to-br ${getStressColor()}`}
          >
            <span className="text-5xl font-bold text-white">{stressLevel}</span>
          </div>

          <p className="mb-6 text-xl font-medium text-gray-700">
            {getStressLabel()}
          </p>

          <div className="w-full max-w-md px-4">
            <input
              type="range"
              min="1"
              max="10"
              value={stressLevel}
              onChange={(e) => setStressLevel(parseInt(e.target.value))}
              className="h-3 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-500"
            />
            <div className="mt-2 flex justify-between text-sm text-gray-500">
              <span>{t('pages.onboarding.stressLevel.low')}</span>
              <span>{t('pages.onboarding.stressLevel.high')}</span>
            </div>
          </div>
        </div>

        <Form className="shrink-0" method="post" onSubmit={handleSubmit}>
          <Button
            title={t('shared.continue')}
            type="submit"
            dataTestId="button-continue"
          />
        </Form>
      </div>
    </Layout>
  )
}
